import { useEffect, useState } from 'react';
import { Brain, Users, ThumbsUp, Timer, Plug } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { trpc } from '@/lib/trpc';
import { toastError } from '@/lib/toast-error';
import { toast } from 'sonner';
import { currentLocale } from '@/lib/locale';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useInternalBrainAdoption } from '@/hooks/useInternalPanel';
import { InternalTable, EmptyState, LoaderBlock, RatioBar, StatCard, fmtDay, fmtMs, fmtNum, fmtDateTime } from './shared';
import { useTranslation } from 'react-i18next';

/**
 * Kill-switch + sample rate de la evaluación automática (juez Haiku sobre las
 * respuestas del Brain). Antes se cambiaba a mano en ConfigTable.
 */
function BrainEvalConfigCard() {
  const { t } = useTranslation();
  const k = 'internal.brainadoptiontab';
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.internal.getBrainEvalConfig.useQuery();
  const [enabled, setEnabled] = useState(true);
  const [samplePct, setSamplePct] = useState('100');
  useEffect(() => {
    if (data) { setEnabled(data.enabled); setSamplePct(String(Math.round(data.sampleRate * 100))); }
  }, [data]);
  const save = trpc.internal.setBrainEvalConfig.useMutation({
    onSuccess: () => { toast.success(t(`${k}.eval_saved`)); utils.internal.getBrainEvalConfig.invalidate(); },
    onError: (e) => toastError(e, 'common.toast.error_al_guardar_configuracion'),
  });
  const pct = Math.min(100, Math.max(0, Number(samplePct) || 0));
  const dirty = !!data && (enabled !== data.enabled || pct !== Math.round(data.sampleRate * 100));
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{t(`${k}.eval_title`)}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{t(`${k}.eval_hint`)}</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} disabled={isLoading || save.isPending} aria-label={t(`${k}.eval_enabled`)} />
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-xs text-muted-foreground">
          {t(`${k}.eval_sample`)}
          <div className="mt-1 flex items-center gap-1">
            <Input type="number" min={0} max={100} step={5} value={samplePct} onChange={(e) => setSamplePct(e.target.value)} disabled={!enabled || isLoading || save.isPending} className="h-8 w-24" />
            <span className="text-sm">%</span>
          </div>
        </label>
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate({ enabled, sampleRate: pct / 100 })}>
          {save.isPending ? t(`${k}.eval_saving`) : t(`${k}.eval_save`)}
        </Button>
        {data?.updatedAt && (
          <span className="text-[11px] text-muted-foreground">
            {t(`${k}.eval_last_change`, { at: new Date(data.updatedAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' }), by: data.updatedBy ?? '—' })}
          </span>
        )}
      </div>
    </Card>
  );
}

type BrainChannels = NonNullable<ReturnType<typeof useInternalBrainAdoption>['data']>['channels'];

/**
 * Corte por canal — fuente: tech_tools_ai.raw_winclap_bedrock_calls
 * (entrypoint + client_app de shared/brain-channel.ts). Muestra chat interno
 * vs. MCP, qué app está del otro lado, quién usa el MCP y sus últimas consultas.
 */
function ChannelBlock({ channels }: { channels?: BrainChannels }) {
  const { t } = useTranslation();
  const k = 'internal.brainadoptiontab';
  // Valores cerrados (shared/brain-channel.ts) + "sin_dato"; un valor nuevo se ve crudo, no vacío.
  const canal = (v: string) => t(`${k}.canal_${v}`, { defaultValue: v });
  const app = (v: string) => t(`${k}.app_${v}`, { defaultValue: v });
  // Deploy desfasado front/back: un backend viejo todavía no manda `channels`.
  // Sin este guard, el throw en render tira abajo TODO /admin/internal (no hay ErrorBoundary).
  if (!channels) return null;
  const maxQ = channels.byChannel[0]?.queries ?? 0;

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-[240px] flex-1">
          <h3 className="text-sm font-semibold">{t(`${k}.canal_titulo`)}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{t(`${k}.canal_hint`)}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <StatCard icon={Plug} label={t(`${k}.consultas_por_mcp`)} value={fmtNum(channels.mcp.queries)} tone="purple" />
          <StatCard icon={Users} label={t(`${k}.usuarios_por_mcp`)} value={fmtNum(channels.mcp.users)} tone="blue" />
        </div>
      </div>

      {channels.error && (
        <p className="mt-3 text-xs text-amber-700">{t(`${k}.canal_error`, { e: channels.error })}</p>
      )}

      {channels.byChannel.length === 0 ? (
        !channels.error && <p className="mt-4 text-xs text-muted-foreground">{t(`${k}.sin_datos_de_canal`)}</p>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div>
            <h4 className="mb-2 text-xs font-semibold text-muted-foreground">{t(`${k}.canal_y_app`)}</h4>
            <div className="space-y-2.5">
              {channels.byChannel.map((c) => (
                <div key={`${c.entrypoint}|${c.clientApp}`}>
                  <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                    <span className="truncate font-medium">
                      {canal(c.entrypoint)} <span className="text-muted-foreground">· {app(c.clientApp)}</span>
                    </span>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      {t(`${k}.n_consultas_m_usuarios_p50`, { n: fmtNum(c.queries), m: fmtNum(c.users), p: fmtMs(c.p50Ms) })}
                    </span>
                  </div>
                  <RatioBar value={c.queries} max={maxQ} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="mb-2 text-xs font-semibold text-muted-foreground">{t(`${k}.top_usuarios_mcp`)}</h4>
            {channels.mcpTopUsers.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t(`${k}.sin_consultas_por_mcp`)}</p>
            ) : (
              <InternalTable
                rows={channels.mcpTopUsers}
                rowKey={(u) => u.userEmail}
                columns={[
                  { key: 'user', label: t(`${k}.usuario`), className: 'max-w-[220px] truncate', render: (u) => u.userEmail },
                  { key: 'queries', label: t(`${k}.consultas`), align: 'right', render: (u) => fmtNum(u.queries) },
                  { key: 'apps', label: t(`${k}.apps`), className: 'text-xs', render: (u) => u.apps.map(app).join(' · ') },
                  { key: 'last', label: t(`${k}.ultima`), align: 'right', className: 'text-xs text-muted-foreground', render: (u) => (u.lastQuery ? fmtDateTime(u.lastQuery) : '—') },
                ]}
              />
            )}
          </div>
        </div>
      )}

      {channels.mcpRecent.length > 0 && (
        <div className="mt-5">
          <h4 className="mb-2 text-xs font-semibold text-muted-foreground">{t(`${k}.ultimas_consultas_mcp`)}</h4>
          <InternalTable
            rows={channels.mcpRecent}
            rowKey={(r, i) => `${r.at}-${i}`}
            columns={[
              { key: 'at', label: t(`${k}.cuando`), className: 'whitespace-nowrap text-xs text-muted-foreground', render: (r) => (r.at ? fmtDateTime(r.at) : '—') },
              { key: 'user', label: t(`${k}.usuario`), className: 'max-w-[180px] truncate', render: (r) => r.userEmail },
              { key: 'channel', label: t(`${k}.canal`), className: 'whitespace-nowrap text-xs', render: (r) => `${canal(r.entrypoint)} · ${app(r.clientApp)}` },
              { key: 'scope', label: t(`${k}.alcance`), className: 'text-xs', render: (r) => r.clientId },
              { key: 'question', label: t(`${k}.pregunta`), className: 'max-w-[360px] truncate text-xs', title: (r) => r.question, render: (r) => r.question },
              { key: 'latency', label: t(`${k}.latencia`), align: 'right', className: 'text-xs', render: (r) => fmtMs(r.latencyMs) },
            ]}
          />
        </div>
      )}
    </Card>
  );
}

/** Adopción del Brain — fuentes: production_brain.query_logs (chat + feedback) y tech_tools_ai.raw_winclap_bedrock_calls (canal). */
/** `canManage` false (viewers con subtab:internal:adoption): sin la card de configuración de evals, que escribe. */
export function BrainAdoptionTab({ canManage = true }: { canManage?: boolean } = {}) {
  const { t } = useTranslation();
  const [days, setDays] = useState<7 | 30>(30);
  const { data, isLoading } = useInternalBrainAdoption(days);

  if (isLoading) return <LoaderBlock />;
  if (!data) return <EmptyState title={t('internal.brainadoptiontab.sin_datos_del_brain')} />;

  const maxClientQueries = data.byClient[0]?.queries ?? 0;
  const feedbackTotal = data.totals.likes + data.totals.dislikes;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('internal.brainadoptiontab.consultas_reales_al_chat_del_brain')}
        </p>
        <div className="flex gap-1">
          {([7, 30] as const).map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 h-8 rounded-md text-sm border ${
                days === d ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'
              }`}
            >
              {d} {t('internal.brainadoptiontab.dias')}
            </button>
          ))}
        </div>
      </div>

      {/* El filtro de cuentas de prueba se ve: sin esto, el bot de staging era la mitad de la adopción */}
      {(data.excluded.queries > 0 || data.excluded.answers > 0) && (
        <p className="text-xs text-muted-foreground" title={data.excluded.byAccount.map((a) => `${a.userEmail}: ${fmtNum(a.queries + a.answers)}`).join('\n')}>
          {t('internal.brainadoptiontab.excluidas_cuentas_prueba', {
            queries: fmtNum(data.excluded.queries),
            answers: fmtNum(data.excluded.answers),
            accounts: data.excluded.byAccount.map((a) => a.userEmail.split('@')[0]).join(', '),
          })}
        </p>
      )}
      {data.excluded.error && (
        <p className="text-xs text-amber-700">{t('internal.brainadoptiontab.canal_error', { e: data.excluded.error })}</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Brain} label={t('internal.brainadoptiontab.consultas')} value={fmtNum(data.totals.queries)} sub={t('internal.brainadoptiontab.ultimos_n_dias_m_clientes', { n: data.rangeDays, m: fmtNum(data.totals.clients) })} tone="purple" />
        <StatCard icon={Users} label={t('internal.brainadoptiontab.usuarios_unicos')} value={fmtNum(data.totals.users)} tone="blue" />
        <StatCard
          icon={ThumbsUp}
          label="Feedback"
          value={feedbackTotal === 0 ? '—' : `${fmtNum(data.totals.likes)} 👍 / ${fmtNum(data.totals.dislikes)} 👎`}
          sub={data.totals.withCitationsPct != null ? `${data.totals.withCitationsPct.toFixed(0)}% con citas` : undefined}
          tone={data.totals.dislikes > data.totals.likes ? 'rose' : 'green'}
        />
        <StatCard icon={Timer} label={t('internal.brainadoptiontab.latencia_p95')} value={fmtMs(data.totals.p95Ms)} sub={`p50 ${fmtMs(data.totals.p50Ms)}`} tone="amber" />
      </div>

      <ChannelBlock channels={data.channels} />

      {canManage && <BrainEvalConfigCard />}

      {data.totals.queries === 0 ? (
        <EmptyState title={t('internal.brainadoptiontab.sin_consultas_en_el_periodo')} />
      ) : (
        <>
          <Card className="p-5">
            <h3 className="text-sm font-semibold mb-3">{t('internal.brainadoptiontab.consultas_y_usuarios_por_dia')}</h3>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={data.perDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tickFormatter={fmtDay} fontSize={11} />
                <YAxis yAxisId="q" allowDecimals={false} fontSize={11} width={36} />
                <YAxis yAxisId="u" orientation="right" allowDecimals={false} fontSize={11} width={30} />
                <Tooltip labelFormatter={(v) => fmtDay(String(v))} />
                <Legend />
                <Bar yAxisId="q" dataKey="queries" name="Consultas" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                <Line yAxisId="u" type="monotone" dataKey="users" name="Usuarios" stroke="#10b981" dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5">
              <h3 className="text-sm font-semibold mb-3">{t('internal.brainadoptiontab.por_cliente')}</h3>
              <div className="space-y-2.5">
                {data.byClient.map((c) => (
                  <div key={c.clientId}>
                    <div className="flex items-baseline justify-between text-sm mb-1">
                      <span className="font-medium">{c.clientId}</span>
                      <span className="text-xs text-muted-foreground">
                        {t('internal.brainadoptiontab.n_consultas_m_usuarios', { n: fmtNum(c.queries), m: fmtNum(c.users) })}
                      </span>
                    </div>
                    <RatioBar value={c.queries} max={maxClientQueries} />
                  </div>
                ))}
              </div>
              {data.byLanguage.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-4">
                  Idiomas:{' '}
                  {data.byLanguage.map((l) => `${l.language} ${fmtNum(l.queries)}`).join(' · ')}
                </p>
              )}
            </Card>
            <Card className="p-5">
              <h3 className="text-sm font-semibold mb-3">{t('internal.brainadoptiontab.top_usuarios_del_brain')}</h3>
              <InternalTable
                rows={data.topUsers}
                rowKey={(u) => u.userEmail}
                columns={[
                  { key: 'user', label: t('internal.brainadoptiontab.usuario'), className: 'truncate max-w-[220px]', render: (u) => u.userEmail },
                  { key: 'queries', label: t('internal.brainadoptiontab.consultas'), align: 'right', render: (u) => fmtNum(u.queries) },
                  { key: 'clients', label: t('internal.brainadoptiontab.clientes'), align: 'right', render: (u) => fmtNum(u.clients) },
                  { key: 'last', label: t('internal.brainadoptiontab.ultima'), align: 'right', className: 'text-xs text-muted-foreground', render: (u) => (u.lastQuery ? fmtDateTime(u.lastQuery) : '—') },
                ]}
              />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
